import React, { useState } from 'react';
import type { APInvoice, LineItem } from '../types/index';
import { X, Plus, Trash2 } from 'lucide-react';

interface APInvoiceFormProps {
  invoice?: APInvoice;
  onSubmit: (invoice: Partial<APInvoice>) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const APInvoiceForm: React.FC<APInvoiceFormProps> = ({
  invoice,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState<Partial<APInvoice>>(
    invoice || {
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      amount: 0,
      currency: 'AED',
      status: 'draft',
      supplierName: '',
      supplierEmail: '',
      poNumber: '',
      lineItems: [],
    }
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.invoiceNumber?.trim()) {
      newErrors.invoiceNumber = 'Invoice number is required';
    }

    if (!formData.invoiceDate) {
      newErrors.invoiceDate = 'Invoice date is required';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    if (!formData.supplierName?.trim()) {
      newErrors.supplierName = 'Supplier name is required';
    }

    if (!formData.supplierEmail?.trim()) {
      newErrors.supplierEmail = 'Supplier email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.supplierEmail)) {
      newErrors.supplierEmail = 'Invalid email format';
    }

    if (!formData.lineItems || formData.lineItems.length === 0) {
      newErrors.lineItems = 'At least one line item is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleLineItemChange = (index: number, field: string, value: any) => {
    const newLineItems = [...(formData.lineItems || [])];
    const lineItem = newLineItems[index];

    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? parseFloat(value) : lineItem.quantity;
      const unitPrice = field === 'unitPrice' ? parseFloat(value) : lineItem.unitPrice;
      lineItem.lineAmount = quantity * unitPrice;
    }

    if (field === 'taxRate') {
      lineItem.taxRate = parseFloat(value);
      lineItem.taxAmount = lineItem.lineAmount * (lineItem.taxRate / 100);
    }

    // @ts-ignore
    lineItem[field as keyof LineItem] = value as any;
    newLineItems[index] = lineItem;

    setFormData((prev) => ({
      ...prev,
      lineItems: newLineItems,
    }));

    // Update total amount
    const totalAmount = newLineItems.reduce((sum, item) => sum + item.lineAmount, 0);
    setFormData((prev) => ({
      ...prev,
      amount: totalAmount,
    }));
  };

  const addLineItem = () => {
    const newLineItem: LineItem = {
      lineNumber: (formData.lineItems?.length || 0) + 1,
      description: '',
      quantity: 1,
      unitPrice: 0,
      lineAmount: 0,
      taxAmount: 0,
      taxRate: 0,
    };

    setFormData((prev) => ({
      ...prev,
      lineItems: [...(prev.lineItems || []), newLineItem],
    }));
  };

  const removeLineItem = (index: number) => {
    const newLineItems = formData.lineItems?.filter((_, i) => i !== index) || [];
    setFormData((prev) => ({
      ...prev,
      lineItems: newLineItems,
    }));

    // Update total amount
    const totalAmount = newLineItems.reduce((sum, item) => sum + item.lineAmount, 0);
    setFormData((prev) => ({
      ...prev,
      amount: totalAmount,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-96 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {invoice ? 'Edit AP Invoice' : 'Create AP Invoice'}
          </h2>
          <button
            onClick={onCancel}
            disabled={loading}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Invoice Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Invoice Details</h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Invoice Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Number *
                </label>
                <input
                  type="text"
                  value={formData.invoiceNumber || ''}
                  onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
                    errors.invoiceNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="INV-2026-001"
                  disabled={loading}
                />
                {errors.invoiceNumber && (
                  <p className="text-red-600 text-sm mt-1">{errors.invoiceNumber}</p>
                )}
              </div>

              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <select
                  value={formData.currency || 'AED'}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  disabled={loading}
                >
                  <option value="AED">AED</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>

              {/* Invoice Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Date *
                </label>
                <input
                  type="date"
                  value={formData.invoiceDate || ''}
                  onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
                    errors.invoiceDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
                {errors.invoiceDate && (
                  <p className="text-red-600 text-sm mt-1">{errors.invoiceDate}</p>
                )}
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date *
                </label>
                <input
                  type="date"
                  value={formData.dueDate || ''}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
                    errors.dueDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
                {errors.dueDate && (
                  <p className="text-red-600 text-sm mt-1">{errors.dueDate}</p>
                )}
              </div>
            </div>
          </div>

          {/* Supplier Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Supplier Details</h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Supplier Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Supplier Name *
                </label>
                <input
                  type="text"
                  value={formData.supplierName || ''}
                  onChange={(e) => handleInputChange('supplierName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
                    errors.supplierName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Supplier Name"
                  disabled={loading}
                />
                {errors.supplierName && (
                  <p className="text-red-600 text-sm mt-1">{errors.supplierName}</p>
                )}
              </div>

              {/* Supplier Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Supplier Email *
                </label>
                <input
                  type="email"
                  value={formData.supplierEmail || ''}
                  onChange={(e) => handleInputChange('supplierEmail', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
                    errors.supplierEmail ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="supplier@example.com"
                  disabled={loading}
                />
                {errors.supplierEmail && (
                  <p className="text-red-600 text-sm mt-1">{errors.supplierEmail}</p>
                )}
              </div>

              {/* PO Number (Optional) */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purchase Order Number (Optional)
                </label>
                <input
                  type="text"
                  value={formData.poNumber || ''}
                  onChange={(e) => handleInputChange('poNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="PO-2026-001"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Line Items</h3>
              <button
                type="button"
                onClick={addLineItem}
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            {errors.lineItems && (
              <p className="text-red-600 text-sm">{errors.lineItems}</p>
            )}

            <div className="space-y-3 max-h-48 overflow-y-auto">
              {formData.lineItems?.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">Item {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeLineItem(index)}
                      disabled={loading}
                      className="text-red-600 hover:text-red-700 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Description */}
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) =>
                          handleLineItemChange(index, 'description', e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        placeholder="Item description"
                        disabled={loading}
                      />
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          handleLineItemChange(index, 'quantity', e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        min="0"
                        step="0.01"
                        disabled={loading}
                      />
                    </div>

                    {/* Unit Price */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit Price
                      </label>
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) =>
                          handleLineItemChange(index, 'unitPrice', e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        min="0"
                        step="0.01"
                        disabled={loading}
                      />
                    </div>

                    {/* Line Amount (Read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Line Amount
                      </label>
                      <input
                        type="number"
                        value={item.lineAmount.toFixed(2)}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                      />
                    </div>

                    {/* Tax Rate */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tax Rate (%)
                      </label>
                      <input
                        type="number"
                        value={item.taxRate}
                        onChange={(e) =>
                          handleLineItemChange(index, 'taxRate', e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        min="0"
                        max="100"
                        step="0.01"
                        disabled={loading}
                      />
                    </div>

                    {/* Tax Amount (Read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tax Amount
                      </label>
                      <input
                        type="number"
                        value={item.taxAmount.toFixed(2)}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total Amount */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
              <span className="text-2xl font-bold text-blue-600">
                {formData.currency} {(formData.amount || 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                'Save Invoice'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
